import { map, rights } from 'fp-ts/lib/Array';
import { fold, getOrElse, left } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import * as t from 'io-ts';
import { Reporter } from 'io-ts/lib/Reporter';
import { Map } from 'immutable';

type HasProps<P extends t.Props> = t.TypeC<P> | t.ExactC<t.TypeC<P>>;

// Helper taken from https://github.com/gcanti/io-ts/issues/332 comments
const getProps = <P extends t.Props>(codec: HasProps<P>): P => {
  // eslint-disable-next-line no-underscore-dangle
  switch (codec._tag) {
    case 'InterfaceType':
      return codec.props;
    case 'ExactType':
      return getProps(codec.type);
  }
};

/**
 * Helper for checking that a value has now values not specified in the props
 * of the codec.
 */
export const hasNoExcess = <P extends t.Props>(
  codec: HasProps<P>,
  value: {[idx: string]: unknown},
) => {
  const props = getProps(codec);
  return Object.keys(value).reduce((acc: boolean, prop: string) => (
    acc && props.hasOwnProperty(prop)
  ), true);
};

export function parseError(error: t.ValidationError): string {
  const path = error.context.map((c, index) => {
    if (index === 0) {
      // the first context is the parent type so the key is always "". In that
      // case we just display the type name instead.
      return c.type.name;
    } else {
      return c.key;
    }
  }).join('/');
  const message = error.message || `Invalid value: ${JSON.stringify(error.value)}`;
  const type = error.context[error.context.length - 1].type.name;
  return `${path}: ${type} - ${message}`;
}

/**
 * This replaces PathReporter so that we have nicer error messages, the
 * downsides of the standard PathReporter are that errors with custom messages
 * don't show the path at all and that the path itself is cluttered with the
 * types, compare:
 *
 * ErrorReporter:
 *   /deployInfo/revision: NonEmptyString - Invalid value: null
 *   /deployInfo/date: DateString - Not a valid date string.
 *
 * PathReporter:
 *   Invalid value null supplied to : ClientConfigs/deployInfo: DeployInfo/revision: NonEmptyString
 *   Not a valid date string.
 */
export const ErrorReporter: Reporter<string[]> = {
  report: fold(
    (errors) => errors.map(parseError),
    () => ['No errors'],
  ),
};

/**
 * Helper for extrapolating the value of a type. Requires an error handler for dealing with
 * the list of errors produced by PathReporter.
 */
export const decodeOrThrow = <I, A>(
  type: t.Decoder<I, A>,
  value: I,
  // Require this to avoid usage of a try/catch block, which is not type safe as of TS 3.5.2.
  errorHandler: (errors: string[]) => never = (errors) => {
    throw new Error(`Error(s) when parsing ${type.name}:\n${errors.join('\n')}`);
  },
): A => decodeOrElse(type, value, (l) => errorHandler(ErrorReporter.report(left(l))));

export const decodeOrElse = <I, A, R> (
  type: t.Decoder<I, A>,
  value: I,
  elseCase: (l: t.Errors) => A | R,
  ): A | R => {
  return pipe(
    type.decode(value),
    getOrElse(elseCase),
  );
};

export const decodeWithDefault = <I, A, R> (
  type: t.Decoder<I, A>,
  value: I,
  defaultValue: R,
): A | R => decodeOrElse(type, value, () => defaultValue);

export const asMarshaller = <A, O, I>(type: t.Type<A, O, I>) => ({
  serialize: (a: A) => {
    if (!type.is(a)) {
      throw new Error(`Expected serialize for ${type.name} to be called on a(n) ${type.name}: given ${a}`);
    }
    return type.encode(a);
  },
  deserialize: (i: I) => decodeOrThrow(type, i),
});

/**
 * Attempts to decode all elements of `input` and filters out those
 * that didn't decode successfuly
 */
export const decodeAndFilter = <A, O, I>(
  type: t.Type<A, O, I>,
  input: I[],
): A[] => pipe(
  input,
  map(type.decode),
  rights,
);

export const makeInt = (x: unknown) => decodeOrThrow(t.Int, x);

export const MapCodec = <K, V>(
  keyCodec: t.Type<K>,
  valueCodec: t.Type<V>,
  name = 'MapCodec',
) => new t.Type<Map<K, V>, Array<[K, V]>, Array<[K, V]>>(
  name,
  (input): input is Map<K, V> => {
    return (
      Map.isMap(input) &&
      input.keySeq().every(k => keyCodec.is(k)) &&
      input.valueSeq().every(v => valueCodec.is(v))
    );
  },
  (input) => {
    return t.success(Map<K, V>(input));
  },
  (input) => input.entrySeq().toArray(),
);

export const FilteredReadonlyArrayCodec = <A, O, I>(
  itemCodec: t.Type<A, O, I>,
  name: string = `FilteredReadonlyArrayCodec[${itemCodec.name}]`,
) => {
  return new t.Type<readonly A[], readonly O[], I>(
    name,
    (input): input is A[] => (
      Array.isArray(input) &&
      input.every(value => itemCodec.is(value))
    ),
    (input, context) => {
      if (!Array.isArray(input)) {
        return t.failure(input, context);
      }

      const filtered = decodeAndFilter(itemCodec, input);
      return t.success(filtered);
    },
    (value) => value.map(itemCodec.encode),
  );
};

